import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios';
import Info from './Info';
import PageStatus from '../PageStatus/PageStatus';
import ApiOrderCard from './ApiOrderCard';
import NavbarHome from '../Navbar/NavbarHome';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';
import OutletSearch from '../SelectSearch/OutletSearch';
import { MdSync } from 'react-icons/md';

const ApiLiveOrders = ({
    token,
    baseUrl,
}) => {
    const [list, setList] = useState([]);
    const [info, setInfo] = useState({});
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [selectedOutlet, setSelectedOutlet] = useState();

    const navigate = useNavigate();

    const handleReport = () => {
        navigate('/report');
    };

    const handleItemReport = () => {
        navigate('/items-report');
    };

    const handleRefresh = async () => {
        fetchStats(selectedOutlet, token)
        fetchCompleted(selectedOutlet, token)
    }

    // ── 2. Fetch stats ─────────────────────────────────────────────────────────
    const fetchStats = useCallback(async (outlet, tok) => {
        if (!outlet || !tok) return;
        await axios.post(`${baseUrl}/stats`, { outlet, token: tok })
            .then(res => setInfo(res.data))
            .catch(err => console.error("Stats error:", err));
    }, [baseUrl]);

    // ── 3. Fetch completed orders ──────────────────────────────────────────────
    const fetchCompleted = useCallback(
        async (outlet, tok) => {
            if (!outlet || !tok) return;

            try {
                setLoading(true);
                setErrorMsg("");

                const res = await axios.post(`${baseUrl}/get-live`, {
                    outlet_name: outlet,
                    token: tok,
                });

                const payload = res.data?.data ?? res.data;

                if (Array.isArray(payload) && payload.length > 0) {
                    setList(payload);
                } else {
                    setList([]);
                    setErrorMsg("No new orders found for this outlet.");
                }
            } catch (err) {
                const msg =
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    "Failed to fetch new orders.";

                setErrorMsg(msg);
                setList([]);
            } finally {
                setLoading(false);
            }
        },
        [baseUrl]
    );

    // ── 4. Trigger fetches when outlet changes ─────────────────────────────────
    useEffect(() => {
        if (!selectedOutlet || !token) return;
        fetchCompleted(selectedOutlet, token);
        fetchStats(selectedOutlet, token);
    }, [selectedOutlet, token, fetchCompleted, fetchStats]);

    return (
        <div className='min-h-screen flex flex-col'>
            <NavbarHome />

            <div className="px-5 flex-grow w-full my-4">
                <div className='grid grid-cols-3 gap-4 mb-3'>
                    <div>
                        <button
                            onClick={handleRefresh}
                            className="
                                flex gap-2 items-center
                                px-3 py-2 text-sm rounded-md font-medium
                                bg-cyan-100 text-cyan-800
                                hover:bg-cyan-200 hover:shadow-md
                                active:bg-cyan-300 active:shadow-sm active:scale-95
                                transition-all duration-150
                            "
                        >
                            <MdSync className="h-5 w-5" />
                            Refresh
                        </button>
                    </div>

                    <div className='flex justify-center'>
                        <OutletSearch
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            token={token}
                        />
                    </div>

                    <div className="flex gap-4 justify-end">
                        <button
                            className="
                                px-3 py-2 text-sm rounded-md font-medium
                                bg-blue-100 text-blue-800
                                hover:bg-blue-200 hover:shadow-md
                                active:bg-blue-300 active:shadow-sm active:scale-95
                                transition-all duration-150
                            "
                            onClick={handleReport}
                        >
                            Report
                        </button>

                        <button
                            className="
                                px-3 py-2 text-sm rounded-md font-medium
                                bg-violet-100 text-violet-800
                                hover:bg-violet-200 hover:shadow-md
                                active:bg-violet-300 active:shadow-sm active:scale-95
                                transition-all duration-150
                            "
                            onClick={handleItemReport}
                        >
                            Item Report
                        </button>
                    </div>
                </div>

                <PageStatus
                    loading={loading}
                    error={errorMsg}
                    loadingText="Loading new orders..."
                />

                <Info info={info} selectedOutlet={selectedOutlet} />

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 md:gap-4 mt-4">
                    {!loading && !errorMsg && list.map((element, index) => (
                        <ApiOrderCard
                            element={element}
                            index={index}
                        />
                    ))}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ApiLiveOrders;